import { useEffect, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit, X, Download, Upload, Camera, Loader2, FolderTree, FileDown, FileUp } from "lucide-react";
import { ManageCategoriesDialog } from "./ManageCategoriesDialog";

const blank = { name: "", description: "", image_url: "", category: "", base_price: 0, variants: [], active: true };

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (!url ? "" : url.startsWith("/api/") ? `${BACKEND}${url}` : url);

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [catOpen, setCatOpen] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  const loadCategories = () => api.get("/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); loadCategories(); }, []);

  const openCreate = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description || "", image_url: p.image_url || "",
      category: p.category || "", base_price: p.base_price || 0, variants: p.variants || [], active: p.active,
    });
    setEditingId(p.id); setOpen(true);
  };

  const submit = async () => {
    try {
      const body = { ...form, base_price: parseFloat(form.base_price) || 0,
        variants: form.variants.map((v) => ({ ...v, price: parseFloat(v.price) || 0, stock: parseInt(v.stock) || 0, options: v.options || {} })) };
      if (editingId) await api.put(`/admin/products/${editingId}`, body);
      else await api.post("/admin/products", body);
      toast.success(editingId ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const addVariant = () => setForm({ ...form, variants: [...form.variants, { name: "", price: 0, stock: 0, options: {} }] });
  const updVariant = (i, k, v) => {
    const next = [...form.variants]; next[i] = { ...next[i], [k]: v }; setForm({ ...form, variants: next });
  };
  const rmVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const importFromZoho = async () => {
    if (!window.confirm("Pull all active items from Zoho Books and add them as products?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/zoho/import-items");
      toast.success(`Imported ${data.fetched} items · ${data.created} new · ${data.updated} updated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const toggleCatalog = async (id) => {
    try { await api.post(`/admin/products/${id}/toggle-catalog`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [exporting, setExporting] = useState(false);
  const [importingXlsx, setImportingXlsx] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/products/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Product list exported");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingXlsx(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new · ${data.updated} updated${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      load(); loadCategories();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImportingXlsx(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-heading font-semibold tracking-tight" data-testid="admin-products-title">Products</h1>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportExcel} disabled={exporting} variant="outline" className="rounded-sm" data-testid="export-excel-btn">
            <FileDown className={`w-4 h-4 mr-1 ${exporting ? "animate-pulse" : ""}`} strokeWidth={1.5}/>
            {exporting ? "Exporting…" : "Export Excel"}
          </Button>
          <label className="inline-flex">
            <input type="file" accept=".xlsx" onChange={importExcel} className="hidden" data-testid="import-excel-file"/>
            <span className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="import-excel-btn">
              {importingXlsx ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <FileUp className="w-4 h-4 mr-1" strokeWidth={1.5}/>}
              {importingXlsx ? "Importing…" : "Import Excel"}
            </span>
          </label>
          <Button onClick={() => setCatOpen(true)} variant="outline" className="rounded-sm" data-testid="manage-categories-btn">
            <FolderTree className="w-4 h-4 mr-1" strokeWidth={1.5}/> Categories
          </Button>
          <Button onClick={importFromZoho} disabled={importing} variant="outline" className="rounded-sm border-[#0e5a32] text-[#0e5a32] hover:bg-green-50" data-testid="import-zoho-items-btn">
            <Download className={`w-4 h-4 mr-1 ${importing ? "animate-spin" : ""}`} strokeWidth={1.5}/>
            {importing ? "Importing…" : "Import from Zoho"}
          </Button>
          <Button onClick={openCreate} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="admin-new-product-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5}/> New product
          </Button>
        </div>
      </div>

      <div className="border border-gray-200">
        <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
          <div className="col-span-4">Product</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-1">Variants</div>
          <div className="col-span-2 text-right">From</div>
          <div className="col-span-2 text-center">In Catalog</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {products.map((p) => (
          <div key={p.id} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 items-center text-sm" data-testid={`admin-product-${p.id}`}>
            <div className="col-span-4 font-medium">
              {p.name}
              {p.zoho_item_id && <span className="status-pill status-synced ml-2 text-[10px]">zoho</span>}
            </div>
            <div className="col-span-2 text-xs text-gray-600" data-testid={`product-category-${p.id}`}>
              {p.category ? p.category : <span className="text-gray-300">—</span>}
            </div>
            <div className="col-span-1 text-gray-500">{(p.variants || []).length}</div>
            <div className="col-span-2 text-right">{inr(p.base_price)}</div>
            <div className="col-span-2 text-center">
              <button onClick={() => toggleCatalog(p.id)} data-testid={`toggle-catalog-${p.id}`} className={`status-pill ${p.show_in_catalog ? "status-paid" : "bg-gray-100 text-gray-500"} cursor-pointer`}>
                {p.show_in_catalog ? "Visible" : "Hidden"}
              </button>
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="text-gray-400 hover:text-[#002fa7]"><Edit className="w-4 h-4" strokeWidth={1.5}/></button>
              {!p.zoho_item_id && (
                <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
              )}
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="p-8 text-sm text-gray-500">No products yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="form-product-name"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-sm mt-1"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Category</Label>
              <Input
                list="product-categories"
                value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                placeholder="e.g., Cleaning & Hygiene (type new or pick existing)"
                className="rounded-sm mt-1"
                data-testid="form-product-category"
              />
              <datalist id="product-categories">
                {categories.map((c) => <option key={c.id} value={c.name} />)}
              </datalist>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Product image</Label>
              <div className="flex items-start gap-4 mt-2">
                <div className="w-24 h-24 shrink-0 border border-gray-200 rounded-sm bg-gray-50 overflow-hidden flex items-center justify-center" data-testid="product-image-preview">
                  {form.image_url
                    ? <img src={resolveImg(form.image_url)} alt="preview" className="w-full h-full object-cover"/>
                    : <span className="text-[10px] text-gray-300">No image</span>}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <label className="inline-flex">
                      <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="product-image-file"/>
                      <span className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-sm cursor-pointer hover:bg-gray-50">
                        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Upload className="w-4 h-4 mr-1" strokeWidth={1.5}/>}
                        Upload
                      </span>
                    </label>
                    <label className="inline-flex">
                      <input type="file" accept="image/*" capture="environment" onChange={handleUpload} className="hidden" data-testid="product-image-camera"/>
                      <span className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-sm cursor-pointer hover:bg-gray-50">
                        <Camera className="w-4 h-4 mr-1" strokeWidth={1.5}/> Camera
                      </span>
                    </label>
                    {form.image_url && (
                      <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="inline-flex items-center px-3 py-2 text-sm text-gray-500 hover:text-red-600" data-testid="product-image-remove">
                        <X className="w-4 h-4 mr-1"/> Remove
                      </button>
                    )}
                  </div>
                  <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="…or paste an image URL" className="rounded-sm text-xs" data-testid="product-image-url"/>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Base price</Label>
              <Input type="number" value={form.base_price} onChange={(e) => setForm({...form, base_price: e.target.value})} className="rounded-sm mt-1"/>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Variants</Label>
                <Button type="button" size="sm" variant="outline" onClick={addVariant} className="rounded-sm" data-testid="form-add-variant"><Plus className="w-3 h-3 mr-1"/>Add</Button>
              </div>
              <div className="space-y-2">
                {form.variants.map((v, i) => (
                  <div key={`${v.name}-${i}`} className="grid grid-cols-12 gap-2 items-center border border-gray-200 p-2">
                    <Input placeholder="Name (e.g., A5 · Black)" value={v.name} onChange={(e) => updVariant(i, "name", e.target.value)} className="col-span-6 rounded-sm"/>
                    <Input placeholder="Price" type="number" value={v.price} onChange={(e) => updVariant(i, "price", e.target.value)} className="col-span-3 rounded-sm"/>
                    <Input placeholder="Stock" type="number" value={v.stock} onChange={(e) => updVariant(i, "stock", e.target.value)} className="col-span-2 rounded-sm"/>
                    <button onClick={() => rmVariant(i)} className="col-span-1 text-gray-400 hover:text-red-600"><X className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={submit} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="form-product-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ManageCategoriesDialog
        open={catOpen}
        onOpenChange={setCatOpen}
        categories={categories}
        onChanged={() => { loadCategories(); load(); }}
      />
    </div>
  );
}
